import React, { useState, useMemo, useRef, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart } from 'recharts';
import FormulaExplanationLatex from './FormulaExplanationLatex';
import 'katex/dist/katex.min.css';

// Компонент CIE діаграми з кольоровим фоном
const CIEDiagram = ({ oledX, oledY, whiteX, whiteY, dominantWavelength }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Параметри відображення
    const padding = 60;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;
    const xMax = 0.8;
    const yMax = 0.9;
    
    // Функція для переведення координат CIE в пікселі
    const toPixelX = (x) => padding + (x / xMax) * plotWidth;
    const toPixelY = (y) => height - padding - (y / yMax) * plotHeight;
    
    // Очистити canvas
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Спектральний локус (точні координати CIE 1931)
    const spectralLocus = [
      { x: 0.1741, y: 0.0050, wl: 380 },
      { x: 0.1738, y: 0.0050, wl: 390 },
      { x: 0.1733, y: 0.0048, wl: 400 },
      { x: 0.1710, y: 0.0048, wl: 410 },
      { x: 0.1669, y: 0.0051, wl: 420 },
      { x: 0.1528, y: 0.0058, wl: 430 },
      { x: 0.1382, y: 0.0074, wl: 440 },
      { x: 0.1070, y: 0.0155, wl: 450 },
      { x: 0.0816, y: 0.0283, wl: 460 },
      { x: 0.0481, y: 0.0600, wl: 470 },
      { x: 0.0200, y: 0.1327, wl: 480 },
      { x: 0.0088, y: 0.2950, wl: 490 },
      { x: 0.0030, y: 0.5384, wl: 500 },
      { x: 0.0049, y: 0.7502, wl: 510 },
      { x: 0.0232, y: 0.8563, wl: 520 },
      { x: 0.0743, y: 0.9238, wl: 530 },
      { x: 0.1547, y: 0.9334, wl: 540 },
      { x: 0.2296, y: 0.8927, wl: 550 },
      { x: 0.3016, y: 0.8130, wl: 560 },
      { x: 0.3731, y: 0.7170, wl: 570 },
      { x: 0.4441, y: 0.6123, wl: 580 },
      { x: 0.5125, y: 0.5093, wl: 590 },
      { x: 0.5752, y: 0.4152, wl: 600 },
      { x: 0.6270, y: 0.3414, wl: 610 },
      { x: 0.6658, y: 0.2887, wl: 620 },
      { x: 0.6915, y: 0.2548, wl: 630 },
      { x: 0.7079, y: 0.2327, wl: 640 },
      { x: 0.7190, y: 0.2170, wl: 650 },
      { x: 0.7260, y: 0.2059, wl: 660 },
      { x: 0.7300, y: 0.1977, wl: 670 },
      { x: 0.7334, y: 0.1915, wl: 680 },
      { x: 0.7344, y: 0.1866, wl: 690 },
      { x: 0.7347, y: 0.1828, wl: 700 },
    ];
    
    // Малюємо кольоровий градієнт усередині спектрального локусу
    const imageData = ctx.createImageData(plotWidth, plotHeight);
    const data = imageData.data;
    
    for (let px = 0; px < plotWidth; px++) {
      for (let py = 0; py < plotHeight; py++) {
        const x = (px / plotWidth) * xMax;
        const y = ((plotHeight - py) / plotHeight) * yMax;
        
        if (isInsideSpectralLocus(x, y, spectralLocus)) {
          const rgb = chromaticityToRGB(x, y);
          const index = (py * plotWidth + px) * 4;
          data[index] = rgb[0];
          data[index + 1] = rgb[1];
          data[index + 2] = rgb[2];
          data[index + 3] = 255;
        }
      }
    }
    
    ctx.putImageData(imageData, padding, padding);
    
    // Малюємо сітку
    ctx.strokeStyle = '#dddddd';
    ctx.lineWidth = 1;
    
    for (let i = 0; i <= 8; i++) {
      const x = i * 0.1;
      const px = toPixelX(x);
      ctx.beginPath();
      ctx.moveTo(px, padding);
      ctx.lineTo(px, height - padding);
      ctx.stroke();
    }
    
    for (let i = 0; i <= 9; i++) {
      const y = i * 0.1;
      const py = toPixelY(y);
      ctx.beginPath();
      ctx.moveTo(padding, py);
      ctx.lineTo(width - padding, py);
      ctx.stroke();
    }
    
    // Малюємо межу спектрального локусу
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    spectralLocus.forEach((point, i) => {
      const px = toPixelX(point.x);
      const py = toPixelY(point.y);
      if (i === 0) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
    });
    
    // Замикаємо лінією пурпурних кольорів
    ctx.lineTo(toPixelX(spectralLocus[0].x), toPixelY(spectralLocus[0].y));
    ctx.stroke();
    
    // Додаємо мітки довжин хвиль
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 11px Arial';
    
    const labelPoints = [380, 470, 490, 510, 520, 530, 540, 550, 560, 570, 580, 590, 600, 620, 650, 700];
    labelPoints.forEach(wl => {
      const point = spectralLocus.find(p => p.wl === wl);
      if (point) {
        const px = toPixelX(point.x);
        const py = toPixelY(point.y);
        
        // Визначаємо зсув тексту залежно від позиції
        let offsetX = 8, offsetY = -8;
        if (wl < 500) {
          offsetX = -25;
          offsetY = 0;
        } else if (wl > 600) {
          offsetX = 8;
          offsetY = 5;
        }
        
        ctx.fillText(`${wl}`, px + offsetX, py + offsetY);
      }
    });
    
    // Малюємо лінію від білої точки до OLED
    ctx.strokeStyle = '#666666';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(toPixelX(whiteX), toPixelY(whiteY));
    ctx.lineTo(toPixelX(oledX), toPixelY(oledY));
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Позначаємо букви a та b для чистоти кольору
    const midX = (whiteX + oledX) / 2;
    const midY = (whiteY + oledY) / 2;
    ctx.fillStyle = '#333';
    ctx.font = 'italic 12px Arial';
    ctx.fillText('a', toPixelX(midX) - 5, toPixelY(midY) - 5);
    
    // Малюємо точку OLED
    const oledPx = toPixelX(oledX);
    const oledPy = toPixelY(oledY);
    
    // Тінь
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    
    ctx.fillStyle = '#4caf50';
    ctx.beginPath();
    ctx.arc(oledPx, oledPy, 10, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Підпис OLED
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 14px Arial';
    ctx.fillText('Джерело (x,y)', oledPx + 15, oledPy - 8);
    ctx.font = '12px Arial';
    ctx.fillText(`(${oledX.toFixed(3)}, ${oledY.toFixed(3)})`, oledPx + 15, oledPy + 6);
    
    // Малюємо білу точку (D65)
    const whitePx = toPixelX(whiteX);
    const whitePy = toPixelY(whiteY);
    
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(whitePx, whitePy, 8, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Підпис білої точки
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 12px Arial';
    ctx.fillText('Біла точка', whitePx - 35, whitePy - 12);
    
    // Малюємо осі
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    
    // Вісь X
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(width - padding + 10, height - padding);
    ctx.stroke();
    
    // Стрілка X
    ctx.beginPath();
    ctx.moveTo(width - padding + 10, height - padding);
    ctx.lineTo(width - padding + 5, height - padding - 5);
    ctx.lineTo(width - padding + 5, height - padding + 5);
    ctx.closePath();
    ctx.fill();
    
    // Вісь Y
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(padding, padding - 10);
    ctx.stroke();
    
    // Стрілка Y
    ctx.beginPath();
    ctx.moveTo(padding, padding - 10);
    ctx.lineTo(padding - 5, padding - 5);
    ctx.lineTo(padding + 5, padding - 5);
    ctx.closePath();
    ctx.fill();
    
    // Мітки на осях
    ctx.fillStyle = '#000000';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    for (let i = 0; i <= 8; i++) {
      const x = i * 0.1;
      const px = toPixelX(x);
      ctx.fillText(x.toFixed(1), px, height - padding + 20);
    }
    
    ctx.textAlign = 'right';
    for (let i = 0; i <= 9; i++) {
      const y = i * 0.1;
      const py = toPixelY(y);
      ctx.fillText(y.toFixed(1), padding - 10, py + 4);
    }
    
    // Назви осей
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('x-координата кольору', width / 2, height - 10);
    
    ctx.save();
    ctx.translate(15, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('y-координата кольору', 0, 0);
    ctx.restore();
    
  }, [oledX, oledY, whiteX, whiteY]);
  
  // Перевірка чи точка всередині спектрального локусу
  function isInsideSpectralLocus(x, y, locus) {
    // Метод трасування променя
    let inside = false;
    for (let i = 0, j = locus.length - 1; i < locus.length; j = i++) {
      const xi = locus[i].x, yi = locus[i].y;
      const xj = locus[j].x, yj = locus[j].y;
      
      const intersect = ((yi > y) !== (yj > y))
          && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    
    // Додаємо лінію пурпурних кольорів
    if (!inside) {
      const firstPoint = locus[0];
      const lastPoint = locus[locus.length - 1];
      
      // Перевірка чи точка нижче лінії пурпурних
      if (y < firstPoint.y && x >= firstPoint.x && x <= lastPoint.x) {
        const slope = (lastPoint.y - firstPoint.y) / (lastPoint.x - firstPoint.x);
        const yOnLine = firstPoint.y + slope * (x - firstPoint.x);
        if (y >= yOnLine) inside = true;
      }
    }
    
    return inside;
  }
  
  // Конвертація CIE xy в RGB
  function chromaticityToRGB(x, y) {
    const Y = 1.0;
    const X = y !== 0 ? (Y / y) * x : 0;
    const Z = y !== 0 ? (Y / y) * (1 - x - y) : 0;
    
    // XYZ to RGB matrix (sRGB D65)
    let r =  3.2406 * X - 1.5372 * Y - 0.4986 * Z;
    let g = -0.9689 * X + 1.8758 * Y + 0.0415 * Z;
    let b =  0.0557 * X - 0.2040 * Y + 1.0570 * Z;
    
    // Гама-корекція sRGB
    const gammaCorrect = (c) => {
      if (c <= 0.0031308) return 12.92 * c;
      return 1.055 * Math.pow(c, 1/2.4) - 0.055;
    };
    
    r = gammaCorrect(r);
    g = gammaCorrect(g);
    b = gammaCorrect(b);
    
    // Нормалізація якщо значення виходять за межі
    const maxVal = Math.max(r, g, b);
    if (maxVal > 1) {
      r /= maxVal;
      g /= maxVal;
      b /= maxVal;
    }
    
    // Обмеження та конвертація в 0-255
    r = Math.max(0, Math.min(255, Math.round(255 * r)));
    g = Math.max(0, Math.min(255, Math.round(255 * g)));
    b = Math.max(0, Math.min(255, Math.round(255 * b)));
    
    return [r, g, b];
  }
  
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas 
        ref={canvasRef} 
        width={700} 
        height={700} 
        style={{ 
          width: '100%', 
          height: '100%',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      />
      <div style={{ 
        marginTop: '15px', 
        fontSize: '13px', 
        color: '#555',
        textAlign: 'center',
        fontStyle: 'italic'
      }}>
        Діаграма колірності CIE 1931 • Домінуюча довжина хвилі: <strong>{dominantWavelength} нм</strong>
      </div>
    </div>
  );
};

const OLEDCalculator = () => {
  // Константи з лабораторної роботи
  const [constants, setConstants] = useState({
    Km: 683, // лм/Вт
    Smax: 0.01, // м²
    D: 1.72,
    tau: 0.000006,
    AOSVS: 33.64,
    Adioda: 0.0000001,
  });

  // Дані ВАХ
  const [vachData] = useState([
    { voltage: 0.0, current1: 3.125e-9, current2: 8.57e-10 },
    { voltage: 0.2, current1: 1.957e-9, current2: -1.09e-10 },
    { voltage: 0.4, current1: 1.663e-9, current2: -5.0e-10 },
    { voltage: 0.6, current1: 1.005e-9, current2: -2.588e-9 },
    { voltage: 0.8, current1: -7.05e-11, current2: -2.87e-9 },
    { voltage: 1.0, current1: -5.93e-10, current2: -3.128e-9 },
    { voltage: 1.2, current1: -1.101e-9, current2: -3.024e-9 },
    { voltage: 1.4, current1: -8.885e-10, current2: -3.273e-9 },
    { voltage: 1.6, current1: -1.542e-9, current2: -3.221e-9 },
    { voltage: 1.8, current1: -1.212e-9, current2: -3.138e-9 },
    { voltage: 2.0, current1: -9.05e-10, current2: -3.339e-9 },
    { voltage: 2.2, current1: -1.179e-9, current2: -3.326e-9 },
    { voltage: 2.4, current1: -4.235e-10, current2: -3.43e-9 },
    { voltage: 2.6, current1: 4.862e-8, current2: -1.116e-9 },
    { voltage: 2.8, current1: 7.262e-7, current2: -5.144e-9 },
    { voltage: 3.0, current1: 2.792e-6, current2: -6.404e-9 },
    { voltage: 3.2, current1: 6.784e-6, current2: -1.96e-8 },
    { voltage: 3.4, current1: 1.239e-5, current2: -3.299e-8 },
    { voltage: 3.6, current1: 1.894e-5, current2: -4.879e-8 },
    { voltage: 3.8, current1: 2.654e-5, current2: -6.649e-8 },
    { voltage: 4.0, current1: 3.538e-5, current2: -9.386e-8 },
    { voltage: 4.2, current1: 4.574e-5, current2: -1.203e-7 },
    { voltage: 4.4, current1: 5.747e-5, current2: -1.428e-7 },
    { voltage: 4.6, current1: 7.097e-5, current2: -1.812e-7 },
    { voltage: 4.8, current1: 8.651e-5, current2: -2.156e-7 },
    { voltage: 5.0, current1: 0.0001043, current2: -2.593e-7 },
    { voltage: 5.2, current1: 0.0001246, current2: -3.101e-7 },
    { voltage: 5.4, current1: 0.0001477, current2: -3.558e-7 },
    { voltage: 5.6, current1: 0.0001739, current2: -4.195e-7 },
    { voltage: 5.8, current1: 0.0002035, current2: -4.818e-7 },
    { voltage: 6.0, current1: 0.0002371, current2: -5.534e-7 },
    { voltage: 6.2, current1: 0.0002748, current2: -6.342e-7 },
    { voltage: 6.4, current1: 0.0003173, current2: -7.148e-7 },
    { voltage: 6.6, current1: 0.0003648, current2: -8.142e-7 },
    { voltage: 6.8, current1: 0.0004176, current2: -9.104e-7 },
    { voltage: 7.0, current1: 0.0004765, current2: -1.023e-6 },
    { voltage: 7.2, current1: 0.0005436, current2: -1.138e-6 },
    { voltage: 7.4, current1: 0.0006157, current2: -1.273e-6 },
    { voltage: 7.6, current1: 0.0006959, current2: -1.404e-6 },
    { voltage: 7.8, current1: 0.0007856, current2: -1.555e-6 },
    { voltage: 8.0, current1: 0.0008837, current2: -1.712e-6 },
    { voltage: 8.2, current1: 0.0009914, current2: -1.874e-6 },
    { voltage: 8.4, current1: 0.001114, current2: -2.056e-6 },
    { voltage: 8.6, current1: 0.001244, current2: -2.249e-6 },
    { voltage: 8.8, current1: 0.001388, current2: -2.448e-6 },
    { voltage: 9.0, current1: 0.001546, current2: -2.662e-6 },
    { voltage: 9.2, current1: 0.001719, current2: -2.892e-6 },
    { voltage: 9.4, current1: 0.001908, current2: -3.126e-6 },
    { voltage: 9.6, current1: 0.002113, current2: -3.384e-6 },
    { voltage: 9.8, current1: 0.00234, current2: -3.646e-6 },
    { voltage: 10.0, current1: 0.002589, current2: -3.933e-6 },
  ]);

  // Спектральні дані
  const [spectralData] = useState([
    { wavelength: 380, intensity: 0, intensity_eye: 0.0001, intensity_photodiode: 0 },
    { wavelength: 400, intensity: 10, intensity_eye: 0.004, intensity_photodiode: 0.05 },
    { wavelength: 420, intensity: 25, intensity_eye: 0.02, intensity_photodiode: 0.12 },
    { wavelength: 440, intensity: 50, intensity_eye: 0.06, intensity_photodiode: 0.2 },
    { wavelength: 460, intensity: 120, intensity_eye: 0.15, intensity_photodiode: 0.3 },
    { wavelength: 480, intensity: 350, intensity_eye: 0.32, intensity_photodiode: 0.42 },
    { wavelength: 500, intensity: 1200, intensity_eye: 0.71, intensity_photodiode: 0.55 },
    { wavelength: 510, intensity: 2200, intensity_eye: 0.88, intensity_photodiode: 0.62 },
    { wavelength: 520, intensity: 2800, intensity_eye: 0.99, intensity_photodiode: 0.68 },
    { wavelength: 530, intensity: 2750, intensity_eye: 0.98, intensity_photodiode: 0.72 },
    { wavelength: 540, intensity: 2400, intensity_eye: 0.91, intensity_photodiode: 0.76 },
    { wavelength: 550, intensity: 1900, intensity_eye: 0.77, intensity_photodiode: 0.79 },
    { wavelength: 560, intensity: 1300, intensity_eye: 0.58, intensity_photodiode: 0.82 },
    { wavelength: 570, intensity: 800, intensity_eye: 0.41, intensity_photodiode: 0.85 },
    { wavelength: 580, intensity: 450, intensity_eye: 0.27, intensity_photodiode: 0.87 },
    { wavelength: 590, intensity: 220, intensity_eye: 0.16, intensity_photodiode: 0.89 },
    { wavelength: 600, intensity: 100, intensity_eye: 0.10, intensity_photodiode: 0.91 },
    { wavelength: 620, intensity: 30, intensity_eye: 0.04, intensity_photodiode: 0.94 },
    { wavelength: 640, intensity: 10, intensity_eye: 0.015, intensity_photodiode: 0.96 },
    { wavelength: 660, intensity: 3, intensity_eye: 0.006, intensity_photodiode: 0.98 },
    { wavelength: 680, intensity: 1, intensity_eye: 0.002, intensity_photodiode: 0.99 },
    { wavelength: 700, intensity: 0.5, intensity_eye: 0.001, intensity_photodiode: 1.0 },
    { wavelength: 750, intensity: 0.1, intensity_eye: 0.0001, intensity_photodiode: 1.0 },
    { wavelength: 770, intensity: 0, intensity_eye: 0, intensity_photodiode: 1.0 },
  ]);

  // CIE color matching functions
  const cieColorMatchingFunctions = useMemo(() => {
    return spectralData.map(d => {
      const lambda = d.wavelength;
      let x_bar = 0, y_bar = 0, z_bar = 0;
      
      if (lambda >= 380 && lambda <= 780) {
        if (lambda < 442) {
          x_bar = 0.0001 + Math.exp(-Math.pow((lambda - 442) / 30, 2)) * 0.35;
        } else if (lambda < 599) {
          x_bar = 0.35 * Math.exp(-Math.pow((lambda - 442) / 50, 2)) + 
                  1.05 * Math.exp(-Math.pow((lambda - 599) / 37, 2));
        } else {
          x_bar = 1.05 * Math.exp(-Math.pow((lambda - 599) / 37, 2));
        }
        
        y_bar = d.intensity_eye;
        
        if (lambda < 500) {
          z_bar = 1.8 * Math.exp(-Math.pow((lambda - 447) / 35, 2));
        } else {
          z_bar = 0;
        }
      }
      
      return { wavelength: lambda, x_bar, y_bar, z_bar };
    });
  }, [spectralData]);

  // Трапецієподібне інтегрування
  const trapezoidalIntegration = (data, xKey, yKey) => {
    let sum = 0;
    for (let i = 0; i < data.length - 1; i++) {
      const dx = data[i + 1][xKey] - data[i][xKey];
      const avgY = (data[i][yKey] + data[i + 1][yKey]) / 2;
      sum += dx * avgY;
    }
    return sum;
  };

  // Розрахунок CIE координат
  const cieCoordinates = useMemo(() => {
    const X = trapezoidalIntegration(
      spectralData.map((d, i) => ({
        wavelength: d.wavelength,
        product: d.intensity * cieColorMatchingFunctions[i].x_bar
      })),
      'wavelength',
      'product'
    );
    
    const Y = trapezoidalIntegration(
      spectralData.map((d, i) => ({
        wavelength: d.wavelength,
        product: d.intensity * cieColorMatchingFunctions[i].y_bar
      })),
      'wavelength',
      'product'
    );
    
    const Z = trapezoidalIntegration(
      spectralData.map((d, i) => ({
        wavelength: d.wavelength,
        product: d.intensity * cieColorMatchingFunctions[i].z_bar
      })),
      'wavelength',
      'product'
    );
    
    const sum = X + Y + Z;
    
    return {
      X,
      Y,
      Z,
      x: sum !== 0 ? X / sum : 0,
      y: sum !== 0 ? Y / sum : 0
    };
  }, [spectralData, cieColorMatchingFunctions]);

  // Чистота кольору
  const colorPurity = useMemo(() => {
    const x = cieCoordinates.x;
    const y = cieCoordinates.y;
    const x_w = 0.3127;
    const y_w = 0.3290;
    
    let lambda_dom = 520;
    
    const a = Math.sqrt(Math.pow(x - x_w, 2) + Math.pow(y - y_w, 2));
    const b = Math.sqrt(Math.pow(0.15 - x, 2) + Math.pow(0.75 - y, 2));
    const purity = a / (a + b);
    
    return { lambda_dom, purity, a, b };
  }, [cieCoordinates]);

  // Колірна температура
  const colorTemperature = useMemo(() => {
    const x = cieCoordinates.x;
    const y = cieCoordinates.y;
    const n = (x - 0.3320) / (0.1858 - y);
    const CCT = 449 * Math.pow(n, 3) + 3525 * Math.pow(n, 2) + 6823.3 * n + 5520.33;
    return Math.round(CCT);
  }, [cieCoordinates]);

  // Kr
  const Kr = useMemo(() => {
    const numerator = trapezoidalIntegration(
      spectralData.map(d => ({
        wavelength: d.wavelength,
        product: d.intensity * d.intensity_eye
      })),
      'wavelength',
      'product'
    );
    
    const denominator = trapezoidalIntegration(spectralData, 'wavelength', 'intensity');
    return denominator !== 0 ? constants.Km * (numerator / denominator) : 0;
  }, [spectralData, constants.Km]);

  // FF
  const FF = useMemo(() => {
    const numerator = trapezoidalIntegration(
      spectralData.map(d => ({
        wavelength: d.wavelength,
        product: d.intensity * d.intensity_photodiode
      })),
      'wavelength',
      'product'
    );
    
    const denominator = trapezoidalIntegration(spectralData, 'wavelength', 'intensity');
    return denominator !== 0 ? numerator / denominator : 0;
  }, [spectralData]);

  // Яскравість
  const brightnessData = useMemo(() => {
    const { Smax, D, tau, AOSVS, Adioda } = constants;
    
    return vachData.map(point => {
      const coefficient = (Smax * Math.pow(D, 2)) / (FF * tau * AOSVS * Adioda);
      const brightness = Kr * point.current1 * coefficient;
      
      return {
        voltage: point.voltage,
        brightness: brightness,
        current: point.current1,
      };
    });
  }, [vachData, Kr, FF, constants]);

  const getColorFromCIE = (x, y) => {
    const Y = 1;
    const X = (Y / y) * x;
    const Z = (Y / y) * (1 - x - y);
    
    let r =  3.2406 * X - 1.5372 * Y - 0.4986 * Z;
    let g = -0.9689 * X + 1.8758 * Y + 0.0415 * Z;
    let b =  0.0557 * X - 0.2040 * Y + 1.0570 * Z;
    
    r = r > 0.0031308 ? 1.055 * Math.pow(r, 1/2.4) - 0.055 : 12.92 * r;
    g = g > 0.0031308 ? 1.055 * Math.pow(g, 1/2.4) - 0.055 : 12.92 * g;
    b = b > 0.0031308 ? 1.055 * Math.pow(b, 1/2.4) - 0.055 : 12.92 * b;
    
    const max = Math.max(r, g, b, 1);
    r = Math.max(0, Math.min(255, Math.round(255 * r / max)));
    g = Math.max(0, Math.min(255, Math.round(255 * g / max)));
    b = Math.max(0, Math.min(255, Math.round(255 * b / max)));
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  const oledColor = getColorFromCIE(cieCoordinates.x, cieCoordinates.y);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1600px', margin: '0 auto', background: '#f5f5f5' }}>
      <h1 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '10px' }}>
        Розрахунок характеристик OLED
      </h1>
      <p style={{ textAlign: 'center', color: '#7f8c8d', marginBottom: '30px' }}>
        Лабораторна робота №2 з органічної електроніки
      </p>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr 1fr', 
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* Константи */}
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, color: '#34495e', fontSize: '20px' }}>⚙️ Константи</h2>
          <div style={{ display: 'grid', gap: '12px', fontSize: '14px' }}>
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Km (лм/Вт): </label>
              <input 
                type="number" 
                value={constants.Km} 
                onChange={(e) => setConstants({...constants, Km: parseFloat(e.target.value)})}
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Smax (м²): </label>
              <input 
                type="number" 
                step="0.001"
                value={constants.Smax} 
                onChange={(e) => setConstants({...constants, Smax: parseFloat(e.target.value)})}
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>D: </label>
              <input 
                type="number" 
                step="0.01"
                value={constants.D} 
                onChange={(e) => setConstants({...constants, D: parseFloat(e.target.value)})}
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Фотометричні величини */}
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, color: '#2e7d32', fontSize: '20px' }}>📊 Фотометричні величини</h2>
          <div style={{ fontSize: '15px', lineHeight: '2' }}>
            <p style={{ margin: '8px 0' }}>
              <strong>Kr:</strong> 
              <span style={{ marginLeft: '10px', color: '#1976d2', fontWeight: 'bold' }}>
                {Kr.toFixed(2)} лм/Вт
              </span>
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>FF:</strong> 
              <span style={{ marginLeft: '10px', color: '#1976d2', fontWeight: 'bold' }}>
                {FF.toFixed(4)}
              </span>
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>Lmax:</strong> 
              <span style={{ marginLeft: '10px', color: '#d32f2f', fontWeight: 'bold' }}>
                {Math.max(...brightnessData.map(d => d.brightness)).toExponential(2)} кд/м²
              </span>
            </p>
          </div>
        </div>

        {/* Кольорові характеристики */}
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, color: '#7b1fa2', fontSize: '20px' }}>🎨 Кольорові характеристики</h2>
          <div style={{ fontSize: '15px', lineHeight: '2' }}>
            <p style={{ margin: '8px 0' }}>
              <strong>CIE x:</strong> 
              <span style={{ marginLeft: '10px', color: '#1976d2', fontWeight: 'bold' }}>
                {cieCoordinates.x.toFixed(4)}
              </span>
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>CIE y:</strong> 
              <span style={{ marginLeft: '10px', color: '#1976d2', fontWeight: 'bold' }}>
                {cieCoordinates.y.toFixed(4)}
              </span>
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>λdom:</strong> 
              <span style={{ marginLeft: '10px', color: '#2e7d32', fontWeight: 'bold' }}>
                {colorPurity.lambda_dom} нм
              </span>
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>Чистота:</strong> 
              <span style={{ marginLeft: '10px', color: '#f57c00', fontWeight: 'bold' }}>
                {(colorPurity.purity * 100).toFixed(1)}%
              </span>
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>CCT:</strong> 
              <span style={{ marginLeft: '10px', color: '#d32f2f', fontWeight: 'bold' }}>
                {colorTemperature} K
              </span>
            </p>
            <div style={{ 
              marginTop: '15px', 
              padding: '20px', 
              background: oledColor,
              borderRadius: '8px',
              border: '2px solid #ddd',
              textAlign: 'center',
              color: 'white',
              textShadow: '0 0 3px black',
              fontWeight: 'bold'
            }}>
              Колір OLED
            </div>
          </div>
        </div>
      </div>

      {/* Пояснення формул з LaTeX */}
      <FormulaExplanationLatex 
        Kr={Kr}
        FF={FF}
        cieCoordinates={cieCoordinates}
        colorPurity={colorPurity}
        colorTemperature={colorTemperature}
      />

      {/* ВАХ */}
      <div style={{ 
        background: 'white', 
        padding: '25px', 
        borderRadius: '12px',
        marginBottom: '20px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ color: '#34495e', marginTop: 0 }}>📈 Вольт-амперна характеристика (ВАХ)</h2>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={vachData} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              dataKey="voltage" 
              label={{ value: 'Напруга (В)', position: 'insideBottom', offset: -10 }}
              stroke="#555"
            />
            <YAxis 
              label={{ value: 'Струм (А)', angle: -90, position: 'insideLeft' }}
              stroke="#555"
            />
            <Tooltip 
              formatter={(value) => value.toExponential(3) + ' A'}
              contentStyle={{ background: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="current1" 
              stroke="#2196f3" 
              name="Струм 1"
              strokeWidth={3}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="current2" 
              stroke="#4caf50" 
              name="Струм 2"
              strokeWidth={3}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Спектральні характеристики */}
        <div style={{ 
          background: 'white', 
          padding: '25px', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#34495e', marginTop: 0 }}>🌈 Спектральні характеристики</h2>
          <ResponsiveContainer width="100%" height={450}>
            <ComposedChart data={spectralData} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="wavelength" 
                label={{ value: 'Довжина хвилі (нм)', position: 'insideBottom', offset: -10 }}
              />
              <YAxis 
                yAxisId="left"
                label={{ value: 'Інтенсивність', angle: -90, position: 'insideLeft' }}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                label={{ value: 'Чутливість', angle: 90, position: 'insideRight' }}
              />
              <Tooltip />
              <Legend />
              <Area
                yAxisId="left"
                type="monotone" 
                dataKey="intensity" 
                fill="#4caf5033"
                stroke="#4caf50" 
                name="Інтенсивність OLED"
                strokeWidth={3}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="intensity_eye" 
                stroke="#ff5722" 
                name="V(λ) - око"
                strokeWidth={2}
                dot={false}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="intensity_photodiode" 
                stroke="#2196f3" 
                name="Фотодіод"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* CIE діаграма */}
        <div style={{ 
          background: 'white', 
          padding: '25px', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#34495e', marginTop: 0 }}>📐 CIE 1931 Діаграма колірності</h2>
          <div style={{ position: 'relative', width: '100%', height: '550px' }}>
            <CIEDiagram 
              oledX={cieCoordinates.x} 
              oledY={cieCoordinates.y}
              whiteX={0.3127}
              whiteY={0.3290}
              dominantWavelength={colorPurity.lambda_dom}
            />
          </div>
        </div>
      </div>

      {/* Яскравість */}
      <div style={{ 
        background: 'white', 
        padding: '25px', 
        borderRadius: '12px',
        marginBottom: '20px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ color: '#34495e', marginTop: 0 }}>💡 Залежність яскравості від напруги</h2>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={brightnessData} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              dataKey="voltage" 
              label={{ value: 'Напруга (В)', position: 'insideBottom', offset: -10 }}
            />
            <YAxis 
              label={{ value: 'Яскравість L (кд/м²)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              formatter={(value) => value.toExponential(3) + ' кд/м²'}
              contentStyle={{ background: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="brightness"
              fill="#ffc10733"
              stroke="#ffc107"
              strokeWidth={3}
              name="Яскравість"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Висновок */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        padding: '30px', 
        borderRadius: '12px',
        color: 'white',
        boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
      }}>
        <h2 style={{ marginTop: 0 }}>📝 Висновок</h2>
        <div style={{ fontSize: '16px', lineHeight: '1.8' }}>
          <p>
            Досліджена органічна світловипромінююча структура (ОСВС) демонструє 
            <strong> низьку ефективність</strong>. Пристрій має порогову напругу <strong>~2.6 В</strong> і 
            потребує значних струмів для досягнення невисоких показників яскравості.
          </p>
          <p>
            <strong>Колірові характеристики:</strong>
          </p>
          <ul style={{ lineHeight: '1.8' }}>
            <li>Домінуюча довжина хвилі: <strong>{colorPurity.lambda_dom} нм</strong> (зелена область спектру)</li>
            <li>CIE координати: <strong>x = {cieCoordinates.x.toFixed(4)}, y = {cieCoordinates.y.toFixed(4)}</strong></li>
            <li>Чистота кольору: <strong>{(colorPurity.purity * 100).toFixed(1)}%</strong></li>
            <li>Колірна температура: <strong>{colorTemperature} K</strong></li>
          </ul>
          <p>
            <strong>Фотометричні параметри:</strong>
          </p>
          <ul style={{ lineHeight: '1.8' }}>
            <li>Коефіцієнт Kr = <strong>{Kr.toFixed(2)} лм/Вт</strong></li>
            <li>Fill Factor FF = <strong>{FF.toFixed(4)}</strong></li>
            <li>Максимальна яскравість = <strong>{Math.max(...brightnessData.map(d => d.brightness)).toExponential(2)} кд/м²</strong></li>
          </ul>
          <p>
            В ході роботи освоєно методику розрахунку яскравісних та кольорових характеристик 
            OLED на основі експериментальних спектральних даних з використанням діаграми колірності CIE 1931.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OLEDCalculator;